import React, { useState } from 'react'
import { localDateKey } from '../../utils/formatters'
import { MONTHS_ES, DAYS_ES, ACT_EMOJI } from '../../lib/constants'

export function CalendarPicker({ selectedDate, availableDates, viewMonth, onViewMonthChange, onSelect, daySummary = {}, annotatedDays = {} }) {
  const [year, month] = viewMonth.split('-').map(Number)
  const [hoverDay, setHoverDay] = useState(null)   // ISO of the day under the cursor (hover summary)

  const firstDow    = new Date(year, month - 1, 1).getDay()
  const startOffset = (firstDow === 0 ? 6 : firstDow - 1)
  const daysInMonth = new Date(year, month, 0).getDate()

  const cells = []
  for (let i = 0; i < startOffset; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)
  while (cells.length % 7 !== 0) cells.push(null)

  // Check if current month is not in the future
  const monthPrefix    = viewMonth  // YYYY-MM
  const today          = localDateKey(Date.now())
  const monthHasData   = viewMonth <= today.slice(0, 7)

  const prevMonth = () => {
    const d = new Date(year, month - 2, 1)
    onViewMonthChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }
  const nextMonth = () => {
    const d = new Date(year, month, 1)
    onViewMonthChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }

  const hasPrev = true
  const hasNext = viewMonth < today.slice(0, 7)

  // ── Hover summary (left panel) ──────────────────────────────────────────────
  const DAYS3   = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb']
  const MONTHS3 = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
  const fmtHM   = min => min >= 60 ? `${Math.floor(min / 60)} h ${min % 60} min` : `${min} min`
  const fmtClock = ms => { const d = new Date(ms); const p = n => String(n).padStart(2, '0'); return `${p(d.getHours())}:${p(d.getMinutes())}` }
  const focusIso = hoverDay ?? selectedDate
  const fs = focusIso ? daySummary[focusIso] : null
  const ann = focusIso ? annotatedDays[focusIso] : null   // manual analysis for this day, if any
  const act = !fs ? null
    : fs.maxHr >= 110 ? { e: 'Activo', t: 'Actividad intensa',  c: '#ff9f0a' }
    : fs.maxHr >= 90  ? { e: 'Caminata', t: 'Actividad moderada', c: '#ffd60a' }
    :                   { e: 'Reposo', t: 'Mayormente reposo',  c: '#34c759' }
  const focusDt = focusIso ? new Date(focusIso + 'T12:00:00') : null

  const isMobile = window.innerWidth <= 640

  // Chip limpio y consistente (ECG, Sueño, Acelerómetro, actividad)
  const chip = (c) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: c + '22', color: c === '#ffd60a' ? '#a98700' : c,
    borderRadius: 8, padding: '3px 8px', fontSize: 10.5, fontWeight: 700,
  })

  return (
    <div style={{
      background: 'var(--card-bg)',
      border: '1px solid var(--border)',
      borderRadius: 14,
      padding: '10px 12px',
      userSelect: 'none',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: 10
    }}>

      {/* ── Left: hover summary ── */}
      <div style={{
        width: isMobile ? '100%' : 150,
        flexShrink: 0,
        borderRight: isMobile ? 'none' : '1px solid var(--border-soft)',
        borderBottom: isMobile ? '1px solid var(--border-soft)' : 'none',
        paddingRight: isMobile ? 0 : 10,
        paddingBottom: isMobile ? 10 : 0,
        marginBottom: isMobile ? 6 : 0,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {fs ? (
          <>
            <p style={{ margin: '2px 0 7px', paddingBottom: 7, borderBottom: '1px solid var(--border-soft)', fontSize: 12, fontWeight: 800, color: 'var(--text)', textTransform: 'capitalize' }}>
              {DAYS3[focusDt.getDay()]} {focusDt.getDate()} {MONTHS3[focusDt.getMonth()]}
            </p>
            <div style={{ fontSize: 11, color: 'var(--text-soft)', lineHeight: 1.65 }}>
              <div><b style={{ color: 'var(--text)' }}>{fmtHM(fs.totalMin)}</b> <span style={{ color: 'var(--text-faint)' }}>registrados</span></div>
              {fs.firstMs != null && <div style={{ color: 'var(--text-muted)' }}>{fmtClock(fs.firstMs)} – {fmtClock(fs.lastMs)}</div>}
              <div>{fs.sessions} {fs.sessions === 1 ? 'sesión' : 'sesiones'}</div>
              {fs.avgHr != null && <div style={{ marginTop: 2 }}><b style={{ color: 'var(--text)' }}>{fs.avgHr}</b> <span style={{ color: 'var(--text-faint)' }}>ppm prom</span></div>}
              {fs.steps != null && <div style={{ marginTop: 2 }}><b style={{ color: 'var(--text)' }}>{fs.steps.toLocaleString()}</b> <span style={{ color: 'var(--text-faint)' }}>pasos</span></div>}
              {(fs.maxHr > 0 || fs.minHr > 0) && (
                <div style={{ display: 'flex', gap: 10, marginTop: 2 }}>
                  {fs.maxHr > 0 && <span style={{ color: '#ff3b30', fontWeight: 800 }}>{fs.maxHr}<span style={{ color: 'var(--text-faint)', fontWeight: 500 }}> máx</span></span>}
                  {fs.minHr > 0 && <span style={{ color: '#007aff', fontWeight: 800 }}>{fs.minHr}<span style={{ color: 'var(--text-faint)', fontWeight: 500 }}> mín</span></span>}
                </div>
              )}
            </div>
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {act && <span style={chip(act.c)}>{act.t}</span>}
              {fs.hasEcg && <span style={chip('#ff3b30')}>ECG</span>}
              {fs.hasSleep && <span style={chip('#5856d6')}>Sueño</span>}
              {fs.hasAcc && <span style={chip('#2bb3a3')}>Acelerómetro</span>}
            </div>
            {ann ? (
              <div style={{ marginTop: 7, paddingTop: 7, borderTop: '1px solid var(--border-soft)', fontSize: 11, color: '#34c759', fontWeight: 700, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                ✓ Analizado
                {ann.activities.length > 0 && <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>{ann.activities.map(a => ACT_EMOJI[a] || '•').join(' ')}</span>}
              </div>
            ) : (
              <div style={{ marginTop: 7, paddingTop: 7, borderTop: '1px solid var(--border-soft)', fontSize: 11, color: 'var(--text-faint)' }}>
                Sin análisis
              </div>
            )}
          </>
        ) : (
          <p style={{ margin: 'auto 0', fontSize: 11, color: 'var(--text-soft)', textAlign: 'center', lineHeight: 1.5 }}>
            Pasá el cursor por un día para ver su resumen
          </p>
        )}
      </div>

      {/* ── Right: calendar ── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <button onClick={prevMonth} disabled={!hasPrev} style={{ background: 'none', border: 'none', cursor: hasPrev ? 'pointer' : 'default', fontSize: 16, color: hasPrev ? 'var(--text-muted)' : 'var(--track)', padding: '2px 6px', borderRadius: 6, lineHeight: 1 }}>‹</button>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', textTransform: 'capitalize' }}>
            {MONTHS_ES[month - 1].toLowerCase()} {year}
          </span>
          <button onClick={nextMonth} disabled={!hasNext} style={{ background: 'none', border: 'none', cursor: hasNext ? 'pointer' : 'default', fontSize: 16, color: hasNext ? 'var(--text-muted)' : 'var(--track)', padding: '2px 6px', borderRadius: 6, lineHeight: 1 }}>›</button>
        </div>

        {/* Day names */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 2 }}>
          {DAYS_ES.map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: 9, fontWeight: 700, color: 'var(--text-fainter)' }}>{d}</div>
          ))}
        </div>

        {/* No data message */}
        {!monthHasData && (
          <div style={{ textAlign: 'center', padding: '10px 0 4px', color: 'var(--text-soft)', fontSize: 12 }}>
            Sin datos este mes
            {hasPrev && <span style={{ display: 'block', fontSize: 11, marginTop: 3, color: '#007aff', cursor: 'pointer' }} onClick={prevMonth}>← Mes anterior</span>}
          </div>
        )}

        {/* Day cells */}
        {monthHasData && <div onMouseLeave={() => setHoverDay(null)} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
          {cells.map((day, i) => {
            if (!day) return <div key={`e-${i}`} style={{ height: 30 }} />
            const iso         = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
            const hasData     = availableDates.has(iso)
            const dayAcc      = hasData && !!daySummary[iso]?.hasAcc
            const isSelected  = iso === selectedDate
            const isToday     = iso === today
            const isHover     = iso === hoverDay
            const isAnnotated = !!annotatedDays[iso]   // I manually analyzed this day
            const isFuture    = iso > today

            if (isFuture) return (
              <div key={iso} style={{ height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.15 }}>
                <span style={{ fontSize: 11, color: 'var(--text-soft)' }}>{day}</span>
              </div>
            )

            return (
              <button
                key={iso}
                onClick={() => {
                  if (window.innerWidth <= 640 && hoverDay !== iso) {
                    setHoverDay(iso)
                  } else {
                    onSelect(iso)
                  }
                }}
                onMouseEnter={() => setHoverDay(iso)}
                style={{
                  height: 30, width: '100%', border: 'none', borderRadius: 7,
                  fontSize: 12, fontWeight: 700,
                  cursor: 'pointer',
                  background: isSelected ? 'var(--btn-strong)' : isHover ? 'var(--track)' : isToday ? '#007aff18' : hasData ? 'var(--chip-bg)' : 'transparent',
                  color: isSelected ? '#fff' : isToday ? '#007aff' : hasData ? 'var(--text)' : 'var(--text-faint)',
                  position: 'relative',
                  outline: isToday && !isSelected ? '1.5px solid #007aff' : 'none',
                  transition: 'all 0.1s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1,
                  opacity: hasData || isToday || isSelected ? 1 : 0.45,
                }}
              >
                {day}
                {!isSelected && hasData && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <span style={{
                      width: isAnnotated ? 4 : 3, height: isAnnotated ? 4 : 3, borderRadius: '50%',
                      background: isAnnotated ? '#5856d6' : isToday ? '#007aff' : '#34c759',
                      display: 'block',
                    }} />
                    {dayAcc && <span style={{ width: 3, height: 3, borderRadius: '50%', background: '#2bb3a3', display: 'block' }} title="Con acelerómetro" />}
                  </span>
                )}
              </button>
            )
          })}
        </div>}
      </div>
    </div>
  )
}

// ── Week / Month analytical report (recorded hours + period-over-period deltas) ──

